import { useState, useEffect } from 'react'
import { Order } from '../../types'
import { localDb, formatCurrency } from '../../lib/db'
import { useToast } from '../../contexts/ToastContext'

export default function AdminOrders() {
    const [orders, setOrders] = useState<Order[]>([])
    const [filterStatus, setFilterStatus] = useState<string>('all')
    const [searchCode, setSearchCode] = useState('')
    const { showToast } = useToast()

    useEffect(() => {
        loadOrders()
    }, [])

    const loadOrders = () => {
        const all = localDb.orders.getAll()
        setOrders(all.sort((a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        ))
    }

    const filteredOrders = orders.filter(order => {
        const matchesStatus = filterStatus === 'all' || order.status === filterStatus
        const matchesCode = !searchCode ||
            order.pickup_code.toLowerCase().includes(searchCode.toLowerCase()) ||
            order.id.toLowerCase().includes(searchCode.toLowerCase())
        return matchesStatus && matchesCode
    })

    const updateOrderStatus = (orderId: string, newStatus: Order['status']) => {
        localDb.orders.updateStatus(orderId, newStatus)
        loadOrders()
        showToast(`Order marked as ${newStatus}`, 'success')
    }

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const getStatusBadge = (status: Order['status']) => {
        const configs: Record<Order['status'], { label: string; className: string }> = {
            pending: { label: 'Pending Payment', className: 'badge-warning' },
            paid: { label: 'Paid - Awaiting Pickup', className: 'badge-primary' },
            ready: { label: 'Ready for Pickup', className: 'badge-success' },
            completed: { label: 'Completed', className: 'badge-success' },
            cancelled: { label: 'Cancelled', className: 'badge-error' }
        }
        const config = configs[status]
        return <span className={`badge ${config.className}`}>{config.label}</span>
    }

    const getNextAction = (status: Order['status']) => {
        switch (status) {
            case 'pending':
                return { label: 'Mark as Paid', nextStatus: 'paid' as const }
            case 'paid':
                return { label: 'Mark Ready', nextStatus: 'ready' as const }
            case 'ready':
                return { label: 'Complete', nextStatus: 'completed' as const }
            default:
                return null
        }
    }

    const pendingCount = orders.filter(o => o.status === 'pending' || o.status === 'paid').length

    return (
        <div>
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">Orders</h1>
                    <p className="admin-page-subtitle">
                        {pendingCount > 0 ? `${pendingCount} order(s) need attention` : 'All orders processed'}
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="orders-toolbar">
                <input
                    type="text"
                    className="input search-input"
                    placeholder="Search by pickup code or order ID..."
                    value={searchCode}
                    onChange={e => setSearchCode(e.target.value)}
                />

                <div className="status-filters">
                    {['all', 'pending', 'paid', 'ready', 'completed', 'cancelled'].map(status => (
                        <button
                            key={status}
                            className={`filter-btn ${filterStatus === status ? 'active' : ''}`}
                            onClick={() => setFilterStatus(status)}
                        >
                            {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Orders List */}
            {filteredOrders.length === 0 ? (
                <div className="empty-orders">
                    <span>📋</span>
                    <p>No orders found</p>
                </div>
            ) : (
                <div className="orders-list">
                    {filteredOrders.map(order => {
                        const nextAction = getNextAction(order.status)

                        return (
                            <div key={order.id} className={`order-card status-${order.status}`}>
                                <div className="order-header">
                                    <div className="order-id-info">
                                        <span className="order-id">#{order.id.slice(0, 8).toUpperCase()}</span>
                                        <span className="order-date">{formatDate(order.created_at)}</span>
                                    </div>
                                    <div className="pickup-code-large">{order.pickup_code}</div>
                                    {getStatusBadge(order.status)}
                                </div>

                                <div className="order-items">
                                    {order.items?.map(item => (
                                        <div key={item.id} className="order-item-row">
                                            <span className="item-qty">{item.quantity}x</span>
                                            <span className="item-name">{item.product_name}</span>
                                            <span className="item-price">{formatCurrency(item.price * item.quantity)}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="order-footer">
                                    <div className="order-totals">
                                        <span className="total-label">Total:</span>
                                        <span className="total-amount">{formatCurrency(order.total)}</span>
                                        {order.s_coins_used > 0 && (
                                            <span className="scoins-used-badge">🪙 {order.s_coins_used} used</span>
                                        )}
                                    </div>

                                    <div className="order-actions">
                                        {nextAction && (
                                            <button
                                                className="btn btn-primary btn-sm"
                                                onClick={() => updateOrderStatus(order.id, nextAction.nextStatus)}
                                            >
                                                {nextAction.label}
                                            </button>
                                        )}
                                        {order.status !== 'cancelled' && order.status !== 'completed' && (
                                            <button
                                                className="btn btn-ghost btn-sm"
                                                onClick={() => updateOrderStatus(order.id, 'cancelled')}
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            <style>{`
        .orders-toolbar {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-4);
          margin-bottom: var(--space-6);
          align-items: center;
        }
        
        .orders-toolbar .search-input {
          max-width: 320px;
        }
        
        .status-filters {
          display: flex;
          gap: var(--space-1);
          flex-wrap: wrap;
        }
        
        .filter-btn {
          padding: var(--space-2) var(--space-4);
          font-size: var(--text-sm);
          font-weight: 500;
          color: var(--text-secondary);
          background: var(--bg-glass);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-full);
          cursor: pointer;
          transition: all var(--transition-fast);
        }
        
        .filter-btn:hover {
          color: var(--text-primary);
          border-color: var(--border-color-hover);
        }
        
        .filter-btn.active {
          color: white;
          background: var(--gradient-primary);
          border-color: transparent;
        }
        
        .empty-orders {
          text-align: center;
          padding: var(--space-12);
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-xl);
        }
        
        .empty-orders span {
          font-size: 3rem;
          display: block;
          margin-bottom: var(--space-2);
        }
        
        .empty-orders p {
          color: var(--text-muted);
        }
        
        .orders-list {
          display: flex;
          flex-direction: column;
          gap: var(--space-4);
        }
        
        .order-card {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-xl);
          overflow: hidden;
        }
        
        .order-card.status-pending,
        .order-card.status-paid {
          border-left: 4px solid var(--color-warning);
        }
        
        .order-card.status-ready {
          border-left: 4px solid var(--color-success);
        }
        
        .order-header {
          display: flex;
          align-items: center;
          gap: var(--space-4);
          padding: var(--space-4);
          background: var(--bg-glass);
          border-bottom: 1px solid var(--border-color);
        }
        
        .order-id-info {
          display: flex;
          flex-direction: column;
        }
        
        .order-id {
          font-family: monospace;
          font-weight: 600;
        }
        
        .order-date {
          font-size: var(--text-xs);
          color: var(--text-muted);
        }
        
        .pickup-code-large {
          margin-left: auto;
          padding: var(--space-2) var(--space-4);
          background: var(--gradient-primary);
          border-radius: var(--radius-lg);
          font-family: var(--font-display);
          font-size: var(--text-lg);
          font-weight: 700;
          color: white;
          letter-spacing: 0.1em;
        }
        
        .order-items {
          padding: var(--space-4);
        }
        
        .order-item-row {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          padding: var(--space-2) 0;
          font-size: var(--text-sm);
        }
        
        .order-item-row:not(:last-child) {
          border-bottom: 1px dashed var(--border-color);
        }
        
        .item-qty {
          color: var(--text-muted);
          min-width: 32px;
        }
        
        .item-name {
          flex: 1;
        }
        
        .item-price {
          font-weight: 500;
        }
        
        .order-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-4);
          background: var(--bg-glass);
          border-top: 1px solid var(--border-color);
        }
        
        .order-totals {
          display: flex;
          align-items: center;
          gap: var(--space-3);
        }
        
        .total-label {
          color: var(--text-muted);
        }
        
        .total-amount {
          font-family: var(--font-display);
          font-size: var(--text-xl);
          font-weight: 700;
        }
        
        .scoins-used-badge {
          padding: var(--space-1) var(--space-2);
          background: rgba(245, 158, 11, 0.1);
          border-radius: var(--radius-sm);
          font-size: var(--text-xs);
          color: var(--color-accent);
        }
        
        .order-actions {
          display: flex;
          gap: var(--space-2);
        }
        
        @media (max-width: 768px) {
          .orders-toolbar {
            flex-direction: column;
            align-items: stretch;
          }
          
          .orders-toolbar .search-input {
            max-width: 100%;
          }
          
          .order-header {
            flex-wrap: wrap;
          }
          
          .pickup-code-large {
            margin-left: 0;
          }
          
          .order-footer {
            flex-direction: column;
            gap: var(--space-3);
          }
        }
      `}</style>
        </div>
    )
}
